"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "zudoku/ui/Button";
import { Input } from "zudoku/ui/Input";
import { Card, CardContent } from "zudoku/ui/Card";
import { useCache } from "zudoku/hooks";
import {
  type ApiEnvironment,
  getToken,
  setToken,
  getApiBaseUrl,
} from "../lib/tokenStorage";

interface UserInfo {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

interface VerificationResult {
  user: UserInfo;
  organizations: Array<{ id: string; name: string }>;
}

interface EnvConfig {
  key: ApiEnvironment;
  label: string;
  host: string;
  tokenUrl?: string;
}

interface TokenState {
  inputValue: string;
  savedToken: string;
  userInfo: VerificationResult | null;
}

const ENVIRONMENTS: EnvConfig[] = [
  {
    key: "cloud",
    label: "Production",
    host: "api.preo.cloud",
    tokenUrl: "https://backoffice.preo.cloud/profile/tokens",
  },
  {
    key: "dev",
    label: "Development",
    host: "api.preo.dev",
  },
];

async function validateToken(
  env: ApiEnvironment,
  token: string
): Promise<VerificationResult | null> {
  try {
    const baseUrl = getApiBaseUrl(env);
    const response = await fetch(`${baseUrl}/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export default function ApiCredentialsForm() {
  const { invalidateCache } = useCache();
  const [activeEnv, setActiveEnv] = useState<ApiEnvironment>("cloud");
  const [tokenStates, setTokenStates] = useState<Record<ApiEnvironment, TokenState>>({
    dev: { inputValue: "", savedToken: "", userInfo: null },
    cloud: { inputValue: "", savedToken: "", userInfo: null },
  });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTokensOnMount = useCallback(async () => {
    const newStates: Record<ApiEnvironment, TokenState> = {
      dev: { inputValue: "", savedToken: "", userInfo: null },
      cloud: { inputValue: "", savedToken: "", userInfo: null },
    };

    for (const env of ["dev", "cloud"] as ApiEnvironment[]) {
      const token = getToken(env);
      if (token) {
        const userInfo = await validateToken(env, token);
        if (userInfo) {
          newStates[env] = { inputValue: token, savedToken: token, userInfo };
        } else {
          setToken(env, "");
        }
      }
    }

    setTokenStates(newStates);
  }, []);

  useEffect(() => {
    loadTokensOnMount();
  }, [loadTokensOnMount]);

  useEffect(() => {
    setError(null);
    setShowToken(false);
  }, [activeEnv]);

  const currentState = tokenStates[activeEnv];
  const activeConfig = ENVIRONMENTS.find((e) => e.key === activeEnv)!;
  const hasValidToken = !!currentState.userInfo;
  const hasUnsavedChanges = currentState.inputValue !== currentState.savedToken;

  const handleInputChange = (value: string) => {
    setTokenStates((prev) => ({
      ...prev,
      [activeEnv]: { ...prev[activeEnv], inputValue: value },
    }));
    setError(null);
  };

  const handleSave = async () => {
    const token = currentState.inputValue.trim();

    if (!token) {
      setToken(activeEnv, "");
      setTokenStates((prev) => ({
        ...prev,
        [activeEnv]: { inputValue: "", savedToken: "", userInfo: null },
      }));
      invalidateCache("API_IDENTITIES");
      return;
    }

    setSaving(true);
    setError(null);

    const userInfo = await validateToken(activeEnv, token);

    if (userInfo) {
      setToken(activeEnv, token);
      setTokenStates((prev) => ({
        ...prev,
        [activeEnv]: { inputValue: token, savedToken: token, userInfo },
      }));
      invalidateCache("API_IDENTITIES");
    } else {
      setToken(activeEnv, "");
      setTokenStates((prev) => ({
        ...prev,
        [activeEnv]: { ...prev[activeEnv], savedToken: "", userInfo: null },
      }));
      setError("Invalid or expired token");
      invalidateCache("API_IDENTITIES");
    }

    setSaving(false);
  };

  const handleClear = () => {
    setToken(activeEnv, "");
    setTokenStates((prev) => ({
      ...prev,
      [activeEnv]: { inputValue: "", savedToken: "", userInfo: null },
    }));
    setError(null);
    invalidateCache("API_IDENTITIES");
  };

  const isEnvValid = (env: ApiEnvironment) => !!tokenStates[env].userInfo;

  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      {/* Left column - Info */}
      <div className="space-y-3">
        <p className="text-muted-foreground">
          Configure your Personal Access Token to authenticate requests in the
          API playground. Tokens are validated and stored locally in your browser.
        </p>
        <p className="text-sm text-muted-foreground">
          Once configured, select your token from the authentication dropdown
          in the <a href="/api/main" className="text-primary underline hover:no-underline">API Reference</a>.
        </p>
      </div>

      {/* Right column - Form */}
      <Card>
        <CardContent className="pt-4 space-y-4">
          {/* Environment Tabs */}
          <div className="flex gap-1 p-1 bg-muted rounded-lg">
            {ENVIRONMENTS.map((env) => (
              <button
                key={env.key}
                type="button"
                onClick={() => setActiveEnv(env.key)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeEnv === env.key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <div className="flex items-center justify-center gap-1.5">
                  {isEnvValid(env.key) && (
                    <svg
                      className="w-4 h-4 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                  <span>{env.label}</span>
                </div>
                <div className="text-xs opacity-70">{env.host}</div>
              </button>
            ))}
          </div>

          {/* User Info */}
          {currentState.userInfo && (
            <div className="p-3 bg-green-50 dark:bg-green-950 rounded-md text-sm text-green-600 dark:text-green-500 space-y-1">
              <div>
                <span className="opacity-70">Email:</span>{" "}
                {currentState.userInfo.user.email}
              </div>
              {(currentState.userInfo.user.first_name ||
                currentState.userInfo.user.last_name) && (
                <div>
                  <span className="opacity-70">Name:</span>{" "}
                  {[
                    currentState.userInfo.user.first_name,
                    currentState.userInfo.user.last_name,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </div>
              )}
              <div>
                <span className="opacity-70">Organizations:</span>{" "}
                {currentState.userInfo.organizations.length}
              </div>
            </div>
          )}

          {/* Token Input */}
          <div className="relative">
            <Input
              type={showToken ? "text" : "password"}
              value={currentState.inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="pat_..."
              className="font-mono pr-16"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
            >
              {showToken ? "Hide" : "Show"}
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 rounded-md">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
            >
              {saving ? "Validating..." : "Save"}
            </Button>
            {hasValidToken && (
              <Button variant="outline" onClick={handleClear}>
                Clear
              </Button>
            )}
            {activeConfig.tokenUrl && (
              <a
                href={activeConfig.tokenUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3"
              >
                Generate token
              </a>
            )}
          </div>

          {/* Help Text */}
          <p className="text-xs text-muted-foreground">
            Stored locally in your browser. Never sent to our servers.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
